import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import { addFaqsCategory } from 'src/ApiServices';

const AddEditFaqCategoryModal = ({ open, onClose, onSubmit, editingCategory }) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingCategory) {
        setName(editingCategory.name || '');
      } else {
        setName('');
      }
    }
  }, [open, editingCategory]);

  const handleClear = () => {
    setName('');
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Please enter category name');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = new FormData();
      submitData.append('name', name.trim());

      if (editingCategory) {
        submitData.append('id', editingCategory.id);
      }

      await addFaqsCategory(submitData);
      await onSubmit();
    } catch (error) {
      console.error('Error submitting FAQ category:', error);
      toast.error(error.response?.data?.message || 'Failed to save FAQ category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 20 },
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          {editingCategory ? 'Edit FAQ Category' : 'Add New FAQ Category'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            fontSize: { xs: 16, sm: 20 },
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Category Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            placeholder="Enter category name"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: palette.primary.inputborder
                },
                '&:hover fieldset': {
                  borderColor: palette.primary.main
                },
                '&.Mui-focused fieldset': {
                  borderColor: palette.primary.main
                }
              },
              '& .MuiInputLabel-root': {
                color: palette.primary.title
              }
            }}
          />
        </Box>
      </DialogContent>

      {/* Footer Buttons */}
      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          justifyContent: 'flex-end',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClear}
          disabled={submitting}
          sx={{
            borderColor: palette.primary.inputborder,
            color: palette.primary.title,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: palette.primary.hover
            }
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            },
            '&:disabled': {
              backgroundColor: palette.primary.disabled,
              color: palette.text.disabled
            }
          }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <span>{editingCategory ? 'Updating...' : 'Saving...'}</span>
            </Box>
          ) : editingCategory ? (
            'Update Category'
          ) : (
            'Save Category'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditFaqCategoryModal;
